import {distributeIntoColumns, placeholderFallback} from '@online-offer/shared';
import {
  Grid,
  GridItem,
  Heading,
  useResponsivePropValue,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {isNotEmpty} from 'ramda';

import {CategorizedFeature} from '../../utils/getFeatures';
import {FeatureItem} from '../FeatureItem/FeatureItem';

interface FeatureCategoryRowProps {
  category: CategorizedFeature;
  columns: number | ValueByDevice<number>;
}

/**
 * One feature category as a full-width row: the category heading above a fixed number of
 * columns. Items fill each column top-to-bottom before moving to the next one, so the
 * column count is resolved here instead of being left to CSS.
 *
 * Columns that stay empty are always the trailing ones, so dropping them keeps every
 * remaining column in its original grid track.
 */
export function FeatureCategoryRow(props: FeatureCategoryRowProps) {
  const columns = useResponsivePropValue(props.columns);
  const featureColumns = distributeIntoColumns(props.category.features, columns).filter(isNotEmpty);

  return (
    <VStack spacing={5}>
      <Heading size={3}>{placeholderFallback(props.category.label?.label)}</Heading>
      <Grid columns={columns} horizontalSpacing={8}>
        {featureColumns.map((features) => (
          <GridItem key={features[0].const_key} span={1}>
            <VStack spacing={1}>
              {features.map((feature) => (
                <FeatureItem
                  key={feature.const_key}
                  title={feature.labels[0].label}
                  isHighlighted={feature.isHighlighted}
                  data-testid={`categoryItem-[${props.category.categoryKey}]`}
                />
              ))}
            </VStack>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
}
